import {
  ALL_SIZES,
  getFormulaBySize,
  isCategorySynced,
  SIZE_INDEX,
} from "portal/utils/almanac";
import { AlmanacTypeCategory, Formula } from "protos/almanac/almanac";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { DisplayCrop } from "portal/utils/crops";
import { DragDividersHint } from "portal/components/shootTime/DragDividersHint";
import { FormulaCard } from "./FormulaCard";
import { getShootTimeBands } from "portal/utils/shootTime";
import { ShootTimeChart } from "portal/components/shootTime/chart/ShootTimeChart";
import { useTranslation } from "react-i18next";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ParentIcon from "@mui/icons-material/ArrowBackOutlined";
import PasteIcon from "@mui/icons-material/ContentPasteOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

interface Props {
  adminEditing?: boolean;
  category: AlmanacTypeCategory;
  copiedFormula: Formula | undefined;
  copiedSizes: number[] | undefined;
  onChange: (newCategory: AlmanacTypeCategory) => void;
  onClose: () => void;
  onDelete?: () => void;
  setCopiedFormula: (formula: Formula | undefined) => void;
  setCopiedSizes: (sizes: number[] | undefined) => void;
  title: string;
  crops: DisplayCrop[];
  readOnly?: boolean;
}

export const AdvancedCategory: FC<Props> = ({
  category: input,
  copiedFormula,
  copiedSizes,
  onChange,
  onClose,
  onDelete,
  setCopiedFormula,
  setCopiedSizes,
  title,
  crops,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { bandStrokeColors },
    },
  } = useTheme();
  const crop = crops.find((crop) => crop.id === input.type?.category);
  // local changes
  const [updatedCategory, setUpdatedCategory] = useState<
    AlmanacTypeCategory | undefined
  >();

  // sync state
  const [areSizesSynced, setSizesSynced] = useState<boolean>(
    isCategorySynced(input)
  );

  // confirmation dialogs
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [confirmExit, setConfirmExit] = useState<boolean>(false);

  // threshold chart inputs that are currently invalid; gates Save
  const [invalidFieldIds, setInvalidFieldIds] = useState(
    () => new Set<string>()
  );
  // Bumped on paste. An invalid threshold lives in the input's draft, and the
  // committed number often does not change, so a new value prop will not
  // replace that draft.
  const [thresholdInputsKey, setThresholdInputsKey] = useState(0);
  const onFieldValidityChange = useCallback(
    (fieldId: string, isValid: boolean) => {
      setInvalidFieldIds((prev) => {
        if (isValid === !prev.has(fieldId)) {
          return prev;
        }
        const next = new Set(prev);
        if (isValid) {
          next.delete(fieldId);
        } else {
          next.add(fieldId);
        }
        return next;
      });
    },
    []
  );

  // Remount the chart so an invalid draft is dropped even when the pasted
  // number matches the one already committed. NumberInput reports the new
  // field's validity itself.
  const onPasteSizes = useCallback(
    (sizes: readonly number[]) => {
      setUpdatedCategory((prev) => ({
        ...(prev ?? input),
        sizes: [...sizes],
      }));
      setThresholdInputsKey((key) => key + 1);
    },
    [input]
  );

  // visible state
  const category = updatedCategory ?? input;
  const isDisabledCrop = Boolean(crop && !crop.isEnabled);

  const shootTimeBands = useMemo(
    () =>
      getShootTimeBands(t, category.sizes, category.formulas, bandStrokeColors),
    [bandStrokeColors, category.formulas, category.sizes, t]
  );

  return (
    <div className="absolute left-0 top-0 w-full p-8 flex flex-col min-h-full bg-carbon-gray-lightest dark:bg-carbon-gray-dark">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 w-full gap-4">
        {/* Breadcrumb */}
        <Button
          onClick={updatedCategory ? () => setConfirmExit(true) : onClose}
          color="inherit"
          startIcon={<ParentIcon />}
        >
          {t("utils.actions.backLong", {
            subject: capitalize(t("models.almanacs.almanac_one")),
          })}
        </Button>
        {confirmExit && (
          <ConfirmationDialog
            title={t("components.almanac.discard.title")}
            description={t("components.almanac.discard.description", {
              title,
            })}
            destructive
            yesText={t("utils.actions.discard")}
            onClose={() => setConfirmExit(false)}
            onYes={() => onClose()}
          />
        )}

        {/* Title */}
        <Typography
          variant="h4"
          className={isDisabledCrop ? "text-xl italic" : "text-xl"}
          color={isDisabledCrop ? "warning" : undefined}
        >
          {title}{" "}
          {isDisabledCrop ? ` (${t("utils.descriptors.disabled")})` : ""}
        </Typography>
        {/* Controls */}

        <div className="flex items-center gap-4">
          {onDelete && !readOnly && (
            <Button
              variant="contained"
              color="error"
              onClick={() => setConfirmDelete(true)}
              startIcon={<DeleteIcon />}
            >
              {t("utils.actions.delete")}
            </Button>
          )}
          {confirmDelete && (
            <ConfirmationDialog
              title={t("utils.actions.deleteLong", {
                subject: t("models.almanacs.almanac_one"),
              })}
              description={t("components.almanac.delete.description")}
              destructive
              yesText={t("utils.actions.deleteLong", { subject: title })}
              onClose={() => setConfirmDelete(false)}
              onYes={() => onDelete?.()}
            />
          )}
          <Button
            variant="text"
            color="info"
            disabled={!updatedCategory}
            onClick={() => setConfirmExit(true)}
          >
            {t("utils.actions.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!updatedCategory || invalidFieldIds.size > 0}
            startIcon={<SaveIcon />}
            onClick={() => {
              onChange(category);
              setUpdatedCategory(undefined);
              onClose();
            }}
          >
            {t("utils.actions.save")}
          </Button>
        </div>
      </div>

      {/* Thresholds are edited directly on the chart */}
      {!readOnly && (
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 w-full gap-4">
          <DragDividersHint />
          <div className="flex items-center gap-2">
            <Button
              variant="contained"
              color="secondary"
              disabled={invalidFieldIds.size > 0}
              startIcon={<CopyIcon />}
              onClick={() => setCopiedSizes(category.sizes)}
            >
              {t("components.almanac.formulas.copySize")}
            </Button>
            {copiedSizes && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PasteIcon />}
                onClick={() => onPasteSizes(copiedSizes)}
              >
                {t("components.almanac.formulas.pasteSize")}
              </Button>
            )}
          </div>
        </div>
      )}

      {readOnly ? (
        <ShootTimeChart bands={shootTimeBands} formulaVersion="legacy" />
      ) : (
        <ShootTimeChart
          key={thresholdInputsKey}
          bands={shootTimeBands}
          formulaVersion="legacy"
          canEdit
          onSizesChange={(sizes) => setUpdatedCategory({ ...category, sizes })}
          onFieldValidityChange={onFieldValidityChange}
        />
      )}

      {/* Formulas */}
      <div className="flex justify-between items-center mt-8">
        <Typography variant="h4" className="text-xl">
          {t("components.almanac.formulas.title")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              color="info"
              checked={areSizesSynced}
              disabled={readOnly}
              onChange={(event) => {
                const isSynced = event.currentTarget.checked;
                setSizesSynced(isSynced);
                if (isSynced) {
                  const firstTrust = category.formulas[SIZE_INDEX.SMALL];
                  if (!firstTrust) {
                    return;
                  }
                  setUpdatedCategory({
                    ...category,
                    formulas: ALL_SIZES.map(() => firstTrust),
                  });
                }
              }}
            />
          }
          label={t("components.almanac.formulas.sync")}
        />
      </div>
      {areSizesSynced ? (
        <FormulaCard
          readOnly={readOnly}
          copiedFormula={copiedFormula}
          formula={getFormulaBySize(category, SIZE_INDEX.SMALL)}
          onChange={(newFormula) => {
            setUpdatedCategory({
              ...category,
              formulas: [newFormula, newFormula, newFormula],
            });
          }}
          setCopiedFormula={setCopiedFormula}
          title={t("components.almanac.formulas.all")}
        />
      ) : (
        [
          {
            sizeIndex: SIZE_INDEX.SMALL,
            title: t("utils.descriptors.small"),
          },
          {
            sizeIndex: SIZE_INDEX.MEDIUM,
            title: t("utils.descriptors.medium"),
          },
          {
            sizeIndex: SIZE_INDEX.LARGE,
            title: t("utils.descriptors.large"),
          },
        ].map(({ sizeIndex, title }) => (
          <FormulaCard
            key={title}
            readOnly={readOnly}
            copiedFormula={copiedFormula}
            formula={getFormulaBySize(category, sizeIndex)}
            onChange={(newFormula) => {
              const formulas = [...category.formulas];
              formulas[sizeIndex] = newFormula;
              setUpdatedCategory({ ...category, formulas });
            }}
            setCopiedFormula={setCopiedFormula}
            title={title}
          />
        ))
      )}
    </div>
  );
};
